import { openDB, deleteDB, type DBSchema, type IDBPDatabase } from "idb";
import type {
  Conversation,
  Message,
  SettingsEntry,
  StoredDocument,
} from "./types";

// DB_NAME kept as "dpo-agent" for backward compatibility — renaming would orphan
// the documents and conversations already stored by any existing user.
export const DB_NAME = "dpo-agent";
export const DB_VERSION = 1;

export interface PersonalRagDB extends DBSchema {
  documents: {
    key: string;
    value: StoredDocument;
    indexes: { "by-hash": string; "by-uploadedAt": number };
  };
  conversations: {
    key: string;
    value: Conversation;
    indexes: { "by-updatedAt": number };
  };
  messages: {
    key: string;
    value: Message;
    indexes: { "by-conversation": string };
  };
  settings: {
    key: string;
    value: SettingsEntry;
  };
}

let dbPromise: Promise<IDBPDatabase<PersonalRagDB>> | null = null;

export function getDB(): Promise<IDBPDatabase<PersonalRagDB>> {
  if (!dbPromise) {
    dbPromise = openDB<PersonalRagDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          const docs = db.createObjectStore("documents", { keyPath: "id" });
          docs.createIndex("by-hash", "hash", { unique: false });
          docs.createIndex("by-uploadedAt", "uploadedAt");

          const convs = db.createObjectStore("conversations", {
            keyPath: "id",
          });
          convs.createIndex("by-updatedAt", "updatedAt");

          const msgs = db.createObjectStore("messages", { keyPath: "id" });
          msgs.createIndex("by-conversation", "conversationId");

          db.createObjectStore("settings", { keyPath: "key" });
        }
      },
    });
  }
  return dbPromise;
}

/** Close the open connection (if any) and clear the cache.
 *  Tests must call this before deleteDB(), otherwise the delete blocks
 *  indefinitely waiting for the connection to close.
 */
export async function closeDB(): Promise<void> {
  if (dbPromise) {
    const db = await dbPromise;
    db.close();
    dbPromise = null;
  }
}

/** Permanently delete the entire database — documents, conversations, messages,
 *  settings (config + OIDC session). Cross-browser equivalent of clearing the
 *  extension/site storage by hand. Closes the open connection first so the
 *  delete doesn't block; the caller should reload the app afterwards, and
 *  getDB() will recreate an empty DB on next access. */
export async function wipeDatabase(): Promise<void> {
  await closeDB();
  await deleteDB(DB_NAME);
}
